import React, { useState, useEffect, useCallback } from 'react';
import { logsAPI, authAPI } from '../api/api';
import { format } from 'date-fns';

const ACTION_ICONS = {
  camera_request: '📷', camera_granted: '📷✅', camera_denied: '📷❌', camera_session_end: '📷🔴',
  microphone_request: '🎤', microphone_granted: '🎤✅', microphone_denied: '🎤❌',
  screen_share_request: '🖥️', screen_share_granted: '🖥️✅', screen_share_denied: '🖥️❌',
  device_lock: '🔒', device_unlock: '🔓',
  sos_triggered: '🚨', sos_resolved: '✅',
  location_accessed: '📍', app_usage_accessed: '📊',
  alarm_triggered: '🔔', alarm_stopped: '🔇',
  webrtc_call_start: '📹', webrtc_call_end: '📹🔴',
  permissions_granted: '✅', permissions_revoked: '❌',
};

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [children, setChildren] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ childId: '', action: '' });
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.childId) params.childId = filters.childId;
      if (filters.action) params.action = filters.action;
      const res = await logsAPI.getLogs(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {}
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => {
    authAPI.children().then(res => setChildren(res.data.children));
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📋 Access Logs</h2>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{total} total records</div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <select className="form-input form-select" style={{ width: 200 }}
            value={filters.childId} onChange={e => { setFilters(p => ({...p, childId: e.target.value})); setPage(1); }}>
            <option value="">All Children</option>
            {children.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className="form-input form-select" style={{ width: 220 }}
            value={filters.action} onChange={e => { setFilters(p => ({...p, action: e.target.value})); setPage(1); }}>
            <option value="">All Actions</option>
            <option value="camera_request">Camera Request</option>
            <option value="camera_granted">Camera Granted</option>
            <option value="microphone_request">Mic Request</option>
            <option value="device_lock">Device Lock</option>
            <option value="sos_triggered">SOS Triggered</option>
            <option value="location_accessed">Location Accessed</option>
            <option value="alarm_triggered">Alarm Triggered</option>
          </select>
          <button className="btn btn-ghost btn-sm" onClick={() => { setFilters({ childId: '', action: '' }); setPage(1); }}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card">
        {loading ? <div className="spinner" /> : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Child</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No logs found</td></tr>
                  ) : logs.map((log, i) => (
                    <tr key={i}>
                      <td>
                        <span style={{ marginRight: 6 }}>{ACTION_ICONS[log.action] || '📌'}</span>
                        <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td>{log.childId?.name || '—'}</td>
                      <td>
                        <span className={`badge ${
                          log.status === 'completed' || log.status === 'approved' ? 'badge-online' :
                          log.status === 'denied' ? 'badge-danger' :
                          log.status === 'initiated' ? 'badge-info' : 'badge-warning'
                        }`}>{log.status}</span>
                      </td>
                      <td>
                        {log.duration ? `${Math.round(log.duration / 60)}m ${log.duration % 60}s` : '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {format(new Date(log.timestamp), 'MMM d, yyyy HH:mm:ss')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  ← Prev
                </button>
                <span style={{ padding: '7px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13 }}>
                  {page} / {totalPages}
                </span>
                <button className="btn btn-ghost btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{
        marginTop: 20, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: 12, padding: '12px 16px', fontSize: 12, color: 'var(--blue-light)'
      }}>
        🔒 All access logs are stored encrypted and automatically expire after 90 days.
        This log is visible to the child on their device for full transparency.
      </div>
    </div>
  );
}
